import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useCart } from '@/contexts/CartContext';
import { useCurrency } from '@/contexts/CurrencyContext';
import Header from '@/components/storefront/Header';
import Footer from '@/components/storefront/Footer';
import PaymentIcons from '@/components/storefront/PaymentIcons';
import { Helmet } from 'react-helmet-async';
import { Input } from '@/components/ui/input';
import { Minus, Plus, X, ShoppingBag, Tag, ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function CartPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    items, removeItem, updateQuantity, subtotal, total,
    bundleDiscount, couponDiscount, coupon,
    itemCount, loading, applyCoupon, removeCoupon, recoverCart,
  } = useCart();
  const { formatPrice } = useCurrency();
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);

  useEffect(() => {
    const recoverToken = searchParams.get('recover');
    const couponParam = searchParams.get('coupon');
    if (recoverToken) {
      recoverCart(recoverToken).then(ok => {
        if (ok) {
          toast.success('Your cart has been restored!');
          if (couponParam) setTimeout(() => applyCoupon(couponParam), 500);
        } else toast.error('Could not restore this cart');
      });
    }
  }, []);

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    const result = await applyCoupon(couponCode.trim());
    if (result.error) toast.error(result.error);
    else { toast.success('Coupon applied!'); setCouponCode(''); }
    setCouponLoading(false);
  };

  return (
    <div data-testid="cart-page" className="min-h-screen" style={{ backgroundColor: '#FDFBF7' }}>
      <Helmet><title>Your Cart - Florashop</title></Helmet>
      <Header />
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-12">
        <motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="font-serif text-3xl md:text-4xl tracking-tight mb-10" style={{ color: '#000' }}>
          Shopping Cart
        </motion.h1>

        {items.length === 0 ? (
          <div className="text-center py-20">
            <ShoppingBag size={48} strokeWidth={1} className="mx-auto mb-4" style={{ color: 'rgba(0,0,0,0.15)' }} />
            <p className="font-serif text-xl mb-2" style={{ color: '#000' }}>Your cart is empty</p>
            <p className="font-sans text-sm mb-6" style={{ color: '#666' }}>Discover our curated collection</p>
            <button onClick={() => navigate('/shop')} data-testid="empty-cart-shop"
              className="font-sans text-sm font-bold uppercase tracking-wider px-8 py-3"
              style={{ backgroundColor: '#000', color: '#fff' }}>
              Start Shopping
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Items */}
            <div className="lg:col-span-2 space-y-0">
              <div className="border-b pb-3 mb-4" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
                <span className="font-sans text-xs uppercase tracking-wider" style={{ color: '#888' }}>{itemCount} items</span>
              </div>
              {items.map(item => (
                <div key={item.item_id} data-testid={`cartpage-item-${item.item_id}`}
                  className="flex gap-5 py-5 border-b" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
                  <Link to={`/product/${item.product_handle}`}
                    className="w-20 h-24 sm:w-24 sm:h-28 flex-shrink-0 overflow-hidden rounded" style={{ backgroundColor: '#f5f3ef' }}>
                    {item.image_url && <img src={item.image_url} alt="" className="w-full h-full object-cover" />}
                  </Link>
                  <div className="flex-1 min-w-0 flex flex-col justify-between">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-sans text-sm font-medium" style={{ color: '#000' }}>{item.title}</h3>
                        {item.variant_title && <p className="font-sans text-xs mt-0.5" style={{ color: '#888' }}>{item.variant_title}</p>}
                        <p className="font-sans text-sm mt-1" style={{ color: '#555' }}>{formatPrice(item.unit_price)}</p>
                      </div>
                      <button data-testid={`cartpage-remove-${item.item_id}`} onClick={() => removeItem(item.item_id)}
                        className="p-1 hover:bg-[rgba(0,0,0,0.05)] rounded transition-colors">
                        <X size={16} strokeWidth={1.5} style={{ color: '#999' }} />
                      </button>
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center border rounded" style={{ borderColor: 'rgba(0,0,0,0.12)' }}>
                        <button onClick={() => updateQuantity(item.item_id, item.quantity - 1)} className="px-3 py-1.5 hover:bg-[rgba(0,0,0,0.03)]"><Minus size={13} /></button>
                        <span className="px-3 py-1.5 font-sans text-sm text-center min-w-[36px]">{item.quantity}</span>
                        <button onClick={() => updateQuantity(item.item_id, item.quantity + 1)} className="px-3 py-1.5 hover:bg-[rgba(0,0,0,0.03)]"><Plus size={13} /></button>
                      </div>
                      <span className="font-sans text-sm font-bold" style={{ color: '#000' }}>{formatPrice(item.unit_price * item.quantity)}</span>
                    </div>
                  </div>
                </div>
              ))}
              <Link to="/shop" className="inline-block mt-6 font-sans text-sm transition-colors" style={{ color: '#888' }}>
                &larr; Continue Shopping
              </Link>
            </div>

            {/* Summary */}
            <div>
              <div className="sticky top-6 space-y-5">
                {/* Coupon */}
                <div className="p-5 border rounded-lg" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
                  {!coupon ? (
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Tag size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#bbb' }} />
                        <Input data-testid="cartpage-coupon-input" value={couponCode} onChange={e => setCouponCode(e.target.value)}
                          placeholder="Coupon code" className="pl-9 font-sans text-sm rounded-none" style={{ borderColor: 'rgba(0,0,0,0.12)' }}
                          onKeyDown={e => e.key === 'Enter' && handleApplyCoupon()} />
                      </div>
                      <button data-testid="cartpage-apply-coupon" onClick={handleApplyCoupon} disabled={couponLoading}
                        className="px-4 font-sans text-sm border" style={{ borderColor: 'rgba(0,0,0,0.12)', color: '#333' }}>Apply</button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between py-1 px-3 rounded" style={{ backgroundColor: 'rgba(34,197,94,0.06)' }}>
                      <span className="font-sans text-sm font-medium" style={{ color: '#16a34a' }}>{coupon.code} applied</span>
                      <button data-testid="cartpage-remove-coupon" onClick={removeCoupon} className="font-sans text-xs underline" style={{ color: '#888' }}>Remove</button>
                    </div>
                  )}
                </div>

                {/* Totals */}
                <div className="p-5 border rounded-lg space-y-3" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
                  <div className="flex justify-between"><span className="font-sans text-sm" style={{ color: '#888' }}>Subtotal</span><span className="font-sans text-sm">{formatPrice(subtotal)}</span></div>
                  {bundleDiscount > 0 && <div className="flex justify-between"><span className="font-sans text-sm" style={{ color: '#16a34a' }}>Bundle Savings</span><span className="font-sans text-sm" style={{ color: '#16a34a' }}>-{formatPrice(bundleDiscount)}</span></div>}
                  {couponDiscount > 0 && <div className="flex justify-between"><span className="font-sans text-sm" style={{ color: '#16a34a' }}>Discount</span><span className="font-sans text-sm" style={{ color: '#16a34a' }}>-{formatPrice(couponDiscount)}</span></div>}
                  <div className="flex justify-between"><span className="font-sans text-sm" style={{ color: '#888' }}>Shipping</span><span className="font-sans text-sm font-medium" style={{ color: '#16a34a' }}>FREE</span></div>
                  <div className="flex justify-between pt-3 border-t" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
                    <span className="font-sans text-base font-bold">Total</span>
                    <span className="font-sans text-xl font-bold" style={{ color: '#000' }}>{formatPrice(total)}</span>
                  </div>
                </div>

                {/* Checkout Button */}
                <button data-testid="checkout-btn" onClick={() => navigate('/checkout')}
                  className="w-full h-14 font-sans text-[15px] font-bold uppercase tracking-wider rounded-lg flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
                  style={{ backgroundColor: '#000', color: '#fff' }}>
                  <ArrowRight size={18} /> Checkout
                </button>

                <PaymentIcons size={34} className="justify-center" />
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
